import { inject, Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { catchError, Observable, of, switchMap } from 'rxjs';
import { IImageFile, IMAGE_SERVICE } from './image.pipe.model';

const BASE_ICON_PATH = 'assets/img/';

@Pipe({
	name: 'fileThumbnail',
	standalone: true,
})
export class FileThumbnailPipe implements PipeTransform {
	private readonly imageService = inject(IMAGE_SERVICE);
	private readonly domSanitizer = inject(DomSanitizer);

	transform(file: IImageFile): Observable<SafeUrl> {
		if (file) {
			if (file.fileContentType) {
				return this.getThumbnailByContentType(file?.fileContentType, file?.id);
			} else if (file.id) {
				return this.getThumbnailByFileId(file.id);
			}
		}
		return of(this.getDefaultThumbnail());
	}

	private getThumbnailByFileId(fileId: string): Observable<SafeUrl> {
		return this.imageService.findById(fileId).pipe(
			switchMap((document) => {
				return this.getThumbnailByContentType(document.contentType, document.id);
			}),
			catchError(() => of(this.getDefaultThumbnail()))
		);
	}

	private getThumbnailByContentType(contentType: string, fileId: string): Observable<SafeUrl> {
		const lowerContentType = contentType.toLowerCase();
		if (this.isImageType(lowerContentType)) {
			return this.imageService.getImageUrl(fileId).pipe(catchError(() => of(this.getDefaultThumbnail())));
		}
		return of(this.getIconByContentType(lowerContentType));
	}

	private isImageType(contentType: string): boolean {
		const imageTypes = [
			'image/jpeg',
			'image/jpg',
			'image/png',
			'image/gif',
			'image/bmp',
			'image/webp',
			'image/svg+xml',
		];
		return imageTypes.some((type) => contentType.includes(type));
	}

	private getIconByContentType(contentType: string): SafeUrl {
		let iconPath = BASE_ICON_PATH;
		// Excel dosyaları
		if (
			contentType.includes('excel') ||
			contentType.includes('spreadsheet') ||
			contentType.includes('.xls') ||
			contentType.includes('application/vnd.ms-excel') ||
			contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
		) {
			iconPath += 'excel-icon.png';
		}
		// Word dosyaları
		else if (
			contentType.includes('word') ||
			contentType.includes('.doc') ||
			contentType.includes('application/msword') ||
			contentType.includes('application/vnd.openxmlformats-officedocument.wordprocessingml.document')
		) {
			iconPath += 'word-icon.png';
		}
		// PowerPoint dosyaları
		else if (
			contentType.includes('powerpoint') ||
			contentType.includes('presentation') ||
			contentType.includes('.ppt') ||
			contentType.includes('application/vnd.ms-powerpoint') ||
			contentType.includes('application/vnd.openxmlformats-officedocument.presentationml.presentation')
		) {
			iconPath += 'powerpoint-icon.png';
		}
		// PDF dosyaları
		else if (contentType.includes('pdf')) {
			iconPath += 'pdf-icon.png';
		}
		// Metin dosyaları
		else if (contentType.includes('text')) {
			iconPath += 'text-icon.png';
		}
		// Varsayılan dosya ikonu
		else {
			iconPath += 'unknown-file-icon.png';
		}

		return this.domSanitizer.bypassSecurityTrustUrl(iconPath);
	}

	private getDefaultThumbnail(): SafeUrl {
		return this.domSanitizer.bypassSecurityTrustUrl(BASE_ICON_PATH + 'unknown-file-icon.png');
	}
}
